import {
  ColumnType,
  GeneratedAlways,
  Insertable,
  Selectable,
  Updateable,
} from 'kysely';
import { expectTypeOf } from 'expect-type';
import { z, zDecimalNullable, zTimestamp } from '@harvestiq/zod';
import { zDecimalRequired } from '@harvestiq/zod';
import {
  RmaClassCodes,
  RmaCommodityTypeCodes,
  RmaInsurancePlanCodes,
  RmaSubclassCodes,
  RmaLrpTypeCodes,
  RmaLrpTypeCode,
  RmaLrpCommodityCode,
  RmaLrpCommodityCodes,
} from '@harvestiq/constants';
import {
  DbColumnDecimalNotNull,
  DbColumnDecimalNullable,
  DbTimestampGenerated,
  DbTimestampGeneratedAlways,
} from '@harvestiq/utils';

/**
 * Types taken from RMA ADM Layout
 * See: https://pubfs-rma.fpac.usda.gov/pub/References/actuarial_data_master/2026/ADMLayout.zip
 */
export interface DbLrpRateDailyTable {
  id: GeneratedAlways<string>;
  createdAt: DbTimestampGeneratedAlways;
  updatedAt: DbTimestampGenerated;
  recordTypeCode: ColumnType<string, string, string | null>;
  recordCategoryCode: ColumnType<string, string, string | null>;
  reinsuranceYear: ColumnType<number, number, number | null>;
  commodityYear: ColumnType<number, number, number | null>;
  commodityCode: ColumnType<
    RmaLrpCommodityCode,
    RmaLrpCommodityCode,
    RmaLrpCommodityCode | null
  >;
  insurancePlanCode: ColumnType<
    RmaInsurancePlanCodes,
    RmaInsurancePlanCodes,
    RmaInsurancePlanCodes | null
  >;
  stateCode: ColumnType<string, string, string | null>;
  countyCode: ColumnType<string, string, string | null>;
  typeCode: ColumnType<RmaLrpTypeCode, RmaLrpTypeCode, RmaLrpTypeCode | null>;
  practiceCode: ColumnType<string, string, string | null>;
  salesEffectiveDate: ColumnType<string, Date | string, Date | string>;
  endorsementLengthCount: ColumnType<number, number, number | null>;
  coveragePrice: DbColumnDecimalNotNull;
  commodityTypeCode: ColumnType<
    RmaCommodityTypeCodes,
    RmaCommodityTypeCodes,
    RmaCommodityTypeCodes | null
  >;
  classCode: ColumnType<RmaClassCodes, RmaClassCodes, RmaClassCodes | null>;
  subClassCode: ColumnType<
    RmaSubclassCodes,
    RmaSubclassCodes,
    RmaSubclassCodes | null
  >;
  intendedUseCode: ColumnType<string, string, string | null>;
  irrigationPracticeCode: ColumnType<string, string, string | null>;
  croppingPracticeCode: ColumnType<string, string, string | null>;
  organicPracticeCode: ColumnType<string, string, string | null>;
  intervalCode: ColumnType<string, string, string | null>;
  endorsementLengthCode: ColumnType<string, string, string | null>;
  targetLowWeight: DbColumnDecimalNotNull;
  targetHighWeight: DbColumnDecimalNotNull;
  expectedEndingValueAmount: DbColumnDecimalNotNull;
  livestockCoverageLevelPercent: DbColumnDecimalNotNull;
  livestockRate: DbColumnDecimalNotNull;
  costPerCwtAmount: DbColumnDecimalNotNull;
  rmaNoteNumber: ColumnType<number, number, number | null>;
  endDate: ColumnType<string, Date | string, Date | string | null>;
  priceAdjustmentFactor: DbColumnDecimalNullable;
  releasedDate: ColumnType<string, Date | string, Date | string | null>;
  lastReleasedDate: ColumnType<
    string | null,
    Date | string | null,
    Date | string | null
  >;
  deletedDate: ColumnType<
    string | null,
    Date | string | null,
    Date | string | null
  >;
  filingDate: ColumnType<string, Date | string, Date | string | null>;
}

export type DbLrpRateDaily = Selectable<DbLrpRateDailyTable>;
export type DbLrpRateDailyInsert = Insertable<DbLrpRateDailyTable>;
export type DbLrpRateDailyUpdate = Updateable<DbLrpRateDailyTable>;

// SELECT SCHEMA
export const dbLrpRateDailySchema = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  recordTypeCode: z.string(),
  recordCategoryCode: z.string(),
  reinsuranceYear: z.number(),
  commodityYear: z.number(),
  commodityCode: z.nativeEnum(RmaLrpCommodityCodes),
  insurancePlanCode: z.nativeEnum(RmaInsurancePlanCodes),
  stateCode: z.string(),
  countyCode: z.string(),
  typeCode: z.nativeEnum(RmaLrpTypeCodes),
  practiceCode: z.string(),
  salesEffectiveDate: z.string(),
  endorsementLengthCount: z.number(),
  coveragePrice: zDecimalRequired(),
  commodityTypeCode: z.nativeEnum(RmaCommodityTypeCodes),
  classCode: z.nativeEnum(RmaClassCodes),
  subClassCode: z.nativeEnum(RmaSubclassCodes),
  intendedUseCode: z.string(),
  irrigationPracticeCode: z.string(),
  croppingPracticeCode: z.string(),
  organicPracticeCode: z.string(),
  intervalCode: z.string(),
  endorsementLengthCode: z.string(),
  targetLowWeight: zDecimalRequired(),
  targetHighWeight: zDecimalRequired(),
  expectedEndingValueAmount: zDecimalRequired(),
  livestockCoverageLevelPercent: zDecimalRequired(),
  livestockRate: zDecimalRequired(),
  costPerCwtAmount: zDecimalRequired(),
  rmaNoteNumber: z.number(),
  endDate: z.string(),
  priceAdjustmentFactor: zDecimalRequired().nullable(),
  releasedDate: z.string(),
  lastReleasedDate: z.string().nullable(),
  deletedDate: z.string().nullable(),
  filingDate: z.string(),
});
expectTypeOf<DbLrpRateDaily>().toMatchTypeOf<
  z.output<typeof dbLrpRateDailySchema>
>();

// INSERT SCHEMA
export const lrpRateDailyInsertSchema = dbLrpRateDailySchema
  .omit({
    id: true,
    createdAt: true,
  })
  .extend({
    updatedAt: zTimestamp().optional(),
    // Decimal fields are insertable as strings
    coveragePrice: zDecimalRequired(),
    targetLowWeight: zDecimalRequired(),
    targetHighWeight: zDecimalRequired(),
    expectedEndingValueAmount: zDecimalRequired(),
    livestockCoverageLevelPercent: zDecimalRequired(),
    livestockRate: zDecimalRequired(),
    costPerCwtAmount: zDecimalRequired(),
    priceAdjustmentFactor: zDecimalNullable(),
  });
type CreateLrpRateDailyFromSchema = z.input<typeof lrpRateDailyInsertSchema>;
expectTypeOf<keyof CreateLrpRateDailyFromSchema>().toEqualTypeOf<
  keyof DbLrpRateDailyInsert
>;

// UPDATE SCHEMA
export const lrpRateDailyUpdateSchema = lrpRateDailyInsertSchema.partial();
type UpdateLrpRateDailyFromSchema = z.input<typeof lrpRateDailyUpdateSchema>;
expectTypeOf<keyof UpdateLrpRateDailyFromSchema>().toEqualTypeOf<
  keyof DbLrpRateDailyUpdate
>;

// Verify derived type matches kysely
expectTypeOf<DbLrpRateDaily>().branded.toEqualTypeOf<
  z.infer<typeof dbLrpRateDailySchema>
>();
